import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AthleteContext } from "./App";
import FilterTags from "./FilterTags";
import TermsModal from "./TermsModal";
import { buildStravaUrl } from "./stravaAuth";
import ShareButtons from "./ShareButtons";

const API = process.env.REACT_APP_BACKEND_URL;
const isNationalAction = process.env.REACT_APP_IS_NATIONAL === "true";

//===============================
// Sectie-definitie 
//===============================
const TERMS_URL = "/content/deelnemersvoorwaarden";

const SECTIONS = isNationalAction ? [
  {
    key: "personal",
    title: "Persoonlijke gegevens",
    info: "Kies een weergavenaam en schrijf een korte tekst. Dit is zichtbaar op je profielpagina.",
    wizardTitle: "Wie ben jij?",
    wizardDesc: "Kies hoe je wilt worden weergegeven en vertel iets over jezelf."
  },
  {
    key: "participation",
    title: "Deelname aan het event",
    info: "Geef aan of je meedoet aan de grote routerit, een deel ervan, of alleen individuele activiteiten.",
    wizardTitle: "Doe je mee aan het event?",
    wizardDesc: "Kies je vorm van deelname en selecteer eventueel de etappes."
  },
  {
    key: "contact",
    title: "Contact",
    info: "Je e-mailadres is verplicht bij deelname aan een route. We gebruiken het alleen voor praktische informatie en updates.",
    wizardTitle: "Hoe kunnen we je bereiken?",
    wizardDesc: "Laat je e-mailadres achter voor informatie over de etappes."
  },
  {
    key: "target",
    title: "Streefbedrag",
    info: "Stel in hoeveel je via donaties wilt ophalen. Verschijnt als voortgangsbalk op je profielpagina.",
    wizardTitle: "Wat is je streefbedrag?",
    wizardDesc: "Optioneel: geef aan hoeveel donaties je wilt ophalen."
  },
  {
    key: "filters",
    title: "Activiteitsfilters",
    info: "Strava-activiteiten die deze trefwoorden bevatten worden gesynchroniseerd naar de kaart.",
    wizardTitle: "Welke activiteiten tellen mee?",
    wizardDesc: "Strava-activiteiten met deze trefwoorden worden zichtbaar op de kaart."
  },
  {
    key: "story",
    title: "Mijn verhaal (optioneel)",
    info: "Een uitgebreider verhaal, te lezen via een knop op je profielpagina. Helemaal optioneel.",
    wizardTitle: "Wil je een uitgebreider verhaal delen?",
    wizardDesc: "Optioneel: vertel uitgebreider over jezelf of je motivatie. Verschijnt via een knop op je profielpagina."
  },
  {
    key: "terms",
    title: "Voorwaarden",
    info: "Lees de deelnemersvoorwaarden voor je je aanmelding afrondt.",
    wizardTitle: "Bijna klaar!",
    wizardDesc: "Lees en accepteer de deelnemersvoorwaarden om je aanmelding te voltooien."
  }
] : [
  {
    key: "personal",
    title: "Persoonlijke gegevens",
    info: "Kies een weergavenaam en schrijf een korte tekst. Dit is zichtbaar op je profielpagina.",
    wizardTitle: "Wie ben jij?",
    wizardDesc: "Kies hoe je wilt worden weergegeven en vertel iets over jezelf."
  },
  {
    key: "participation",
    title: "Deelname aan het event",
    info: "Geef aan of je meedoet aan de grote routerit of alleen individuele activiteiten.",
    wizardTitle: "Doe je mee aan het event?",
    wizardDesc: "Kies je vorm van deelname en selecteer eventueel de etappes."
  },
  {
    key: "contact",
    title: "Contact",
    info: "Je e-mailadres is verplicht bij deelname aan een route. We gebruiken het alleen voor praktische informatie en updates.",
    wizardTitle: "Hoe kunnen we je bereiken?",
    wizardDesc: "Laat je e-mailadres achter voor informatie over de etappes."
  },
  {
    key: "target",
    title: "Streefbedrag",
    info: "Stel in hoeveel je via donaties wilt ophalen. Verschijnt als voortgangsbalk op je profielpagina.",
    wizardTitle: "Wat is je streefbedrag?",
    wizardDesc: "Optioneel: geef aan hoeveel donaties je wilt ophalen."
  },
  {
    key: "filters",
    title: "Activiteitsfilters",
    info: "Strava-activiteiten die deze trefwoorden bevatten worden gesynchroniseerd naar de kaart.",
    wizardTitle: "Welke activiteiten tellen mee?",
    wizardDesc: "Strava-activiteiten met deze trefwoorden worden zichtbaar op de kaart."
  },
  {
    key: "story",
    title: "Persoonlijke details (optioneel)",
    info: "Een uitgebreider verhaal, te lezen via een knop op je profielpagina. Helemaal optioneel.",
    wizardTitle: "Wil je een uitgebreider verhaal delen?",
    wizardDesc: "Optioneel: vertel uitgebreider over jezelf of je motivatie. Verschijnt via een knop op je profielpagina."
  }
];

//===============================
// Herbruikbare info-popover 
//===============================
function InfoButton({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="ob-info-wrapper">
      <button
        type="button"
        className="ob-info-btn"
        onClick={e => { e.stopPropagation(); setShow(s => !s); }}
        aria-label="Meer informatie"
      >ⓘ</button>
      {show && (
        <div className="ob-info-popup" onClick={() => setShow(false)}>
          {text}
        </div>
      )}
    </span>
  );
}

//===============================
// Sectie-card voor profiel-modus 
//===============================
function SectionCard({ section, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="ob-section-card">
      <div className="ob-section-header" onClick={() => setOpen(o => !o)}>
        <span className="ob-section-title">{section.title}</span>
        <span className="ob-section-right">
          <InfoButton text={section.info} />
          <span className="ob-section-arrow">{open ? "▲" : "▼"}</span>
        </span>
      </div>
      {open && <div className="ob-section-body">{children}</div>}
    </div>
  );
}

//===============================
// Hoofd-component 
//===============================
export default function Onboarding() {
  const { currentAthlete, setCurrentAthlete, sessionChecked } = useContext(AthleteContext);
  const navigate = useNavigate();

  const [csrfToken, setCsrfToken] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const [saved, setSaved] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [showTerms, setShowTerms] = useState(false);

  // Form state
  const [nickname, setNickname] = useState("");
  const [bio, setBio] = useState("");
  const [story, setStory] = useState("");
  const [tags, setTags] = useState([]);
  const [email, setEmail] = useState("");
  const [notifyByEmail, setNotifyByEmail] = useState(false);
  const [targetAmount, setTargetAmount] = useState(0);
  const [participationType, setParticipationType] = useState("none");
  const [participationDays, setParticipationDays] = useState([]);
  const [overnightDays, setOvernightDays] = useState([]);
  const [eventFilters, setEventFilters] = useState([]);
  const [trainingFilters, setTrainingFilters] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(!isNationalAction); // false);

  useEffect(() => {
    fetch(`${API}/api/csrf-token`, { credentials: "include" })
      .then(r => r.json())
      .then(data => setCsrfToken(data.csrfToken));
    fetch(`${API}/event-routes`)
      .then(r => r.json())
      .then(data => setAvailableDays(Array.isArray(data) ? data : []))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!currentAthlete) return;
    setNickname(currentAthlete.nickname || "");
    setBio(currentAthlete.bio || "");
    setStory(currentAthlete.story || "");
    setTags(currentAthlete.tags || []);
    setEmail(currentAthlete.email || "");
    setNotifyByEmail(currentAthlete.notifyByEmail || false);
    setTargetAmount(currentAthlete.targetAmount || 0);
    setParticipationType(currentAthlete.participationType || "none");
    setParticipationDays(currentAthlete.participationDays || []);
    setOvernightDays(currentAthlete.overnightDays || []);
    setEventFilters(currentAthlete.eventFilters || []);
    setTrainingFilters(currentAthlete.trainingFilters || []);
    setTermsAccepted(currentAthlete.termsAccepted || false);
  }, [currentAthlete]);

  // ── Sessie-check ──────────────────────────────────────
  useEffect(() => {
    const check = () => {
      fetch(`${API}/athlete/session`, { credentials: "include" })
        .then(r => r.json())
        .then(data => { if (!data.authenticated) navigate("/aanmelden"); });
    };
    const interval = setInterval(check, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  // ── Helpers ───────────────────────────────────────────
  const allOvernightDays = days => days.slice(0, -1).map(d => d.dayNumber);
  const routeParticipant = participationType === "full" || participationType === "partial";

  const handleTypeChange = type => {
    setParticipationType(type);
    if (type === "full" && availableDays.length > 0) setOvernightDays(allOvernightDays(availableDays));
  };

  const toggleDay = dayNumber => {
    setParticipationDays(prev =>
      prev.includes(dayNumber) ? prev.filter(d => d !== dayNumber) : [...prev, dayNumber].sort((a, b) => a - b)
    );
  };

  const toggleOvernight = dayNumber => {
    setOvernightDays(prev =>
      prev.includes(dayNumber) ? prev.filter(d => d !== dayNumber) : [...prev, dayNumber].sort((a, b) => a - b)
    );
  };

  const validate = () => {
    if (routeParticipant && !email.trim()) {
      alert("Vul je e-mailadres in — verplicht bij deelname aan een route.");
      return false;
    }
    if (!termsAccepted) {
      alert("Accepteer de deelnemersvoorwaarden om door te gaan.");
      return false;
    }
    return true;
  };

  const save = () => {
    if (!validate()) return;
    fetch(`${API}/athlete/onboarding`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "CSRF-Token": csrfToken },
      body: JSON.stringify({
        nickname, bio, story, tags, email, notifyByEmail, targetAmount, termsAccepted,
        eventFilters, trainingFilters,
        participationType, participationDays,
        overnightDays: participationType === "partial"
          ? overnightDays.filter(d => participationDays.includes(d) && participationDays.includes(d + 1))
          : overnightDays
      })
    })
      .then(r => { if (r.status === 401) { navigate("/aanmelden"); return null; } return r.json(); })
      .then(data => {
        if (!data) return;
        setCurrentAthlete(a => ({ ...a, nickname, bio, story, tags, email, notifyByEmail, targetAmount, termsAccepted, eventFilters, trainingFilters, participationType, participationDays, overnightDays, active: true }));
        setSaved(true);
        setTimeout(() => setSaved(false), 4000);
      });
  };

  const logout = () => {
    fetch(`${API}/athlete/logout`, { method: "POST", credentials: "include", headers: { "CSRF-Token": csrfToken } })
      .then(() => { setCurrentAthlete(null); navigate("/"); });
  };

  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [stravaOpen, setStravaOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const cancelParticipation = () => {
    fetch(`${API}/athlete/cancel`, { method: "POST", credentials: "include", headers: { "CSRF-Token": csrfToken } })
      .then(() => { setCurrentAthlete(null); navigate("/"); });
  };

  const [stepError, setStepError] = useState("");

  const validateStep = (step) => {
    const key = SECTIONS[step - 1]?.key;
    if (key === "contact" && routeParticipant && !email.trim()) {
      return "Vul je e-mailadres in — verplicht bij deelname aan een route.";
    }
    return "";
  };

  const nextStep = () => {
    const err = validateStep(wizardStep);
    if (err) { setStepError(err); return; }
    setStepError("");
    setWizardStep(s => Math.min(s + 1, SECTIONS.length));
  };
  const prevStep = () => { setStepError(""); setWizardStep(s => Math.max(s - 1, 1)); };

  // ── Wachten op sessie ─────────────────────────────────
  if (!sessionChecked) return <p>Laden…</p>;
  if (!currentAthlete) {
    return (
      <div className="pane pane-center dark_pane">
        <h1>Niet ingelogd</h1>
        <p>Je bent niet aangemeld. <a href="/aanmelden">Meld je aan via Strava</a> om je profiel in te stellen.</p>
      </div>
    );
  }

  const isFirstTime = !currentAthlete.active;
  const currentSection = SECTIONS[wizardStep - 1];

  // ── Inhoud per sectie ─────────────────────────────────
  const sectionPersonal = (
    <>
      <label htmlFor="nickname">Weergavenaam (optioneel)</label>
      <input id="nickname" value={nickname} onChange={e => setNickname(e.target.value)} placeholder="Naam of nickname" />
      <label htmlFor="bio">Persoonlijke tekst (optioneel)</label>
      <small>Zichtbaar op je profielpagina. Alleen vetgedrukt en cursief zijn toegestaan.</small>
      <textarea
        id="bio" value={bio} onChange={e => setBio(e.target.value)}
        placeholder="Vertel iets over jezelf…" rows={4}
        style={{ width: "100%", boxSizing: "border-box", marginBottom: "0.5rem" }}
      />
    </>
  );

  const sectionParticipation = (
    <div className="onboarding-participation">
      <div className={`ob-option${participationType === "full" ? " selected" : ""}`} onClick={() => handleTypeChange("full")}>
        <div className="ob-option-header">
          <span className="ob-circle">{participationType === "full" ? "✓" : ""}</span>
          <span>Ik doe mee aan de {isNationalAction ? "hele grote" : "grote"} actie</span>
        </div>
        {isNationalAction && participationType === "full" && availableDays.length > 0 && (
          <div className="ob-sub" onClick={e => e.stopPropagation()}>
            <span className="ob-sub-label">Na welke etappes wil je overnachten?</span>
            {availableDays.slice(0, -1).map(day => {
              const on = overnightDays.includes(day.dayNumber);
              return (
                <div key={day.dayNumber} className={`ob-overnight-full${on ? " selected" : ""}`} onClick={() => toggleOvernight(day.dayNumber)}>
                  <span className={`ob-square${on ? " selected" : ""}`}>{on ? "✓" : ""}</span>
                  <span>Na dag {day.dayNumber} — {day.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isNationalAction && (
        <div className={`ob-option${participationType === "partial" ? " selected" : ""}`} onClick={() => handleTypeChange("partial")}>
          <div className="ob-option-header">
            <span className="ob-circle">{participationType === "partial" ? "✓" : ""}</span>
            <span>Ik doe mee aan een deel van de grote actie</span>
          </div>
          {participationType === "partial" && availableDays.length > 0 && (
            <div className="ob-sub" onClick={e => e.stopPropagation()}>
              {availableDays.map((day, idx) => {
                const checked = participationDays.includes(day.dayNumber);
                const isLast = idx === availableDays.length - 1;
                const nextDay = availableDays[idx + 1];
                const canOvernight = checked && !isLast && nextDay && participationDays.includes(nextDay.dayNumber);
                const isOvernighting = overnightDays.includes(day.dayNumber);
                return (
                  <div key={day.dayNumber} className={`ob-day${checked ? " selected" : ""}`} onClick={() => toggleDay(day.dayNumber)}>
                    <div className="ob-day-header">
                      <span className={`ob-square${checked ? " selected" : ""}`}>{checked ? "✓" : ""}</span>
                      <span><strong>Dag {day.dayNumber}</strong> — {day.name}{day.distanceKm > 0 && <small> ({day.distanceKm} km)</small>}</span>
                    </div>
                    {checked && !isLast && (
                      <div
                        className={`ob-overnight${canOvernight && isOvernighting ? " selected" : ""}${!canOvernight ? " disabled" : ""}`}
                        onClick={e => { e.stopPropagation(); if (canOvernight) toggleOvernight(day.dayNumber); }}
                      >
                        <span className={`ob-square${canOvernight && isOvernighting ? " selected" : ""}${!canOvernight ? " disabled" : ""}`}>
                          {canOvernight && isOvernighting ? "✓" : ""}
                        </span>
                        <span>Overnachten na dag {day.dayNumber}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {[
        { value: "individual", label: "Ik doe zelf individuele acties (lopen, fietsen, etc.)" },
        { value: "none", label: "Nog niet besloten" }
      ].map(opt => (
        <div key={opt.value} className={`ob-option${participationType === opt.value ? " selected" : ""}`} onClick={() => handleTypeChange(opt.value)}>
          <div className="ob-option-header">
            <span className="ob-circle">{participationType === opt.value ? "✓" : ""}</span>
            <span>{opt.label}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const sectionContact = (
    <div className="ob-contact-form">
      <div className="ob-contact-field">
        <label htmlFor="email">
          E-mailadres
          {routeParticipant && <span className="ob-required">*</span>}
        </label>
        <input
          id="email" type="email" value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="jouw@emailadres.nl"
        />
        <small className="note">
          {routeParticipant
            ? "Verplicht bij deelname — voor etappe-informatie."
            : "Optioneel — voor updates over de actie."}
          {" "}Wordt niet getoond op de site.
        </small>
      </div>
      <label className="onboarding-check">
        <input type="checkbox" checked={notifyByEmail} onChange={e => setNotifyByEmail(e.target.checked)}
          style={{ display: "inline", width: "auto", margin: 0 }} />
        Stuur mij een melding als iemand doneert
      </label>
    </div>
  );

  const sectionTarget = (
    <>
      <label htmlFor="targetAmount">Streefbedrag (€)</label>
      <small>Hoeveel donaties wil je ophalen? Wordt getoond als voortgangsbalk op je profielpagina.</small>
      <div className="ap-amount-row">
        <span className="ap-eur">€</span>
        <input
          id="targetAmount" type="number" min="0" step="50"
          value={targetAmount || ""} onChange={e => setTargetAmount(Number(e.target.value) || 0)}
          placeholder="0" className="ap-amount-input"
        />
      </div>
    </>
  );

  const sectionFilters = (
    <>
      <label>Activiteitsfilter evenementen</label>
      <small>Zoektermen in naam of beschrijving van Strava-activiteiten.</small>
      <FilterTags filters={eventFilters} onChange={setEventFilters} placeholder="bijv. Hartekind" />
      <label>Activiteitsfilter trainingen</label>
      <small>Zelfde als boven, maar gemarkeerd als training op de kaart.</small>
      <FilterTags filters={trainingFilters} onChange={setTrainingFilters} placeholder="bijv. training" />
    </>
  );

  const sectionStory = (
    <>
      <label htmlFor="story">Mijn verhaal (optioneel)</label>
      <small>Verschijnt via een knop ("Lees meer over mij →") op je profielpagina. Laat leeg als je dit niet wilt tonen.</small>
      <small>Toegestane opmaak: &lt;h2&gt;, &lt;h3&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;p&gt;, &lt;br&gt;, &lt;ul&gt;/&lt;ol&gt;/&lt;li&gt;.</small>
      <textarea
        id="story" value={story} onChange={e => setStory(e.target.value)}
        placeholder="Vertel hier uitgebreider over jezelf of je motivatie…" rows={6}
        style={{ width: "100%", boxSizing: "border-box", marginBottom: "0.5rem" }}
      />
      <label>Tags (optioneel)</label>
      <small>Korte kenmerken, bijv. "hardloper" of "vader van 2".</small>
      <FilterTags filters={tags} onChange={setTags} placeholder="bijv. hardloper" />
    </>
  );

  let sectionContent;

  if (isNationalAction) {
    const sectionTerms = (
      <div>
        <p>Lees de deelnemersvoorwaarden voordat je je aanmelding voltooit.</p>
        <button
          type="button"
          className="btn-secondary"
          style={{ marginBottom: "1rem" }}
          onClick={() => setShowTerms(true)}
        >
          Deelnemersvoorwaarden lezen
        </button>
        <label className={`onboarding-check${termsAccepted ? " ob-accepted" : ""}`}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={e => setTermsAccepted(e.target.checked)}
            style={{ display: "inline", width: "auto", margin: 0 }}
          />
          {termsAccepted
            ? "✓ Akkoord met de deelnemersvoorwaarden"
            : <>Ik ga akkoord met de deelnemersvoorwaarden<span style={{ color: "#c00", marginLeft: 4 }}>*</span></>}
        </label>
      </div>
    );

    sectionContent = { personal: sectionPersonal, participation: sectionParticipation, contact: sectionContact, target: sectionTarget, filters: sectionFilters, story: sectionStory, terms: sectionTerms };
  } else {
    sectionContent = { personal: sectionPersonal, participation: sectionParticipation, contact: sectionContact, target: sectionTarget, filters: sectionFilters, story: sectionStory };
  }

  //console

  // ── Render ────────────────────────────────────────────
  return (
    <div className="pane pane-center dark_pane">
      {showTerms && isNationalAction && (
        <TermsModal
          alreadyAccepted={termsAccepted}
          onClose={() => setShowTerms(false)}
          onAccept={() => { setTermsAccepted(true); setShowTerms(false); }}
        />
      )}
      <div className="profile-header">
        {currentAthlete.profilePhoto && (
          <img src={currentAthlete.profilePhoto} alt={currentAthlete.firstName} className="profile-photo" />
        )}
        <h1>Mijn profiel</h1>
      </div>
      <p>Welkom, <strong>{currentAthlete.firstName} {currentAthlete.lastName}</strong>.</p>

      {!currentAthlete.approved && (
        <p className="pending-notice">
          Je aanmelding wacht nog op goedkeuring door de beheerder. Je routes worden daarna gesynchroniseerd.
        </p>
      )}

      {/* ── Wizard-modus (eerste keer) ── */}
      {isFirstTime ? (
        <div className="wizard">
          <div className="wizard-progress">
            {SECTIONS.map((s, i) => (
              <div key={s.key} className={`wizard-dot${wizardStep === i + 1 ? " active" : wizardStep > i + 1 ? " done" : ""}`} />
            ))}
          </div>
          <div className="wizard-step-header">
            <h2 className="wizard-step-title">{currentSection.wizardTitle}</h2>
            <p className="note">{currentSection.wizardDesc}</p>
          </div>
          <div className="wizard-step-body">
            {sectionContent[currentSection.key]}
          </div>
          {stepError && <p className="content-error" style={{ marginBottom: "0.5rem" }}>{stepError}</p>}
          <div className="wizard-nav">
            <div className="wizard-step-label">Stap {wizardStep} van {SECTIONS.length}</div>
            <div className="wizard-nav-actions">
              {wizardStep > 1 && (
                <button type="button" className="btn-secondary" onClick={prevStep}>← Vorige</button>
              )}
              {wizardStep < SECTIONS.length ? (
                <button type="button" onClick={nextStep}>Volgende →</button>
              ) : (
                <button type="button" onClick={save}>Opslaan en afronden</button>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── Profiel-modus (terugkerend) ── */
        <div className="profile-sections">
          {SECTIONS.map((section, i) => (
            <SectionCard key={section.key} section={section} defaultOpen={i === 0}>
              {sectionContent[section.key]}
            </SectionCard>
          ))}

          <ShareButtons athlete={currentAthlete} />

          <div className="ob-section-card">
            <div className="ob-section-header" onClick={() => setStravaOpen(o => !o)}>
              <span className="ob-section-title">Koppeling met Strava herstellen</span>
              <span className="ob-section-right">
                <span className="ob-section-arrow">{stravaOpen ? "▲" : "▼"}</span>
              </span>
            </div>
            {stravaOpen && (
              <div className="ob-section-body">
                <p>
                  Werkt de synchronisatie van je activiteiten niet goed? Klik op de knop hieronder om je af te
                  melden en opnieuw aan te melden via Strava, zodat de koppeling wordt hersteld.
                </p>
                <p className="content-error">
                  Let op! Vink tijdens het opnieuw aanmelden nooit "View data about your private activities" uit.
                </p>
                <a href={buildStravaUrl(true)} className="btn-secondary">
                  Afmelden en opnieuw aanmelden via Strava
                </a>
              </div>
            )}
          </div>

          <div className="ob-section-card" style={{ borderColor: "#f5c5c5" }}>
            <div className="ob-section-header" style={{ background: "#fdf0f0" }} onClick={() => setCancelOpen(o => !o)}>
              <span className="ob-section-title" style={{ color: "#c00" }}>Deelnemerschap opzeggen</span>
              <span className="ob-section-right">
                <span className="ob-section-arrow">{cancelOpen ? "▲" : "▼"}</span>
              </span>
            </div>
            {cancelOpen && (
              <div className="ob-section-body">
                <p>Wil je je deelnemerschap beëindigen? Je profiel wordt verborgen en de koppeling met Strava wordt verwijderd.</p>
                <p className="content-error">
                  Let op! Hiermee wordt je niet meer getoond op de website en daarmee ook niet meer je Strava activiteiten. Je donaties kunnen echter niet worden teruggevorderd. Deze zijn definitief en worden niet terugbetaald.<br />Wil je dit toch doen, klik dan op de knop hieronder.
                </p>
                <button type="button" className="btn-danger" onClick={() => setCancelConfirm(true)}>
                  Deelnemerschap opzeggen
                </button>
              </div>
            )}
          </div>

          <div className="onboarding-actions">
            <button onClick={save}>Opslaan</button>
            <button className="btn-ghost" onClick={logout}>Afmelden</button>
            {saved && <span className="save-success-inline">✓ Opgeslagen</span>}
          </div>
        </div>
      )}

      {cancelConfirm && (
        <div className="terms-overlay" onClick={() => setCancelConfirm(false)}>
          <div className="terms-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Deelnemerschap opzeggen</h2>
              <button type="button" className="terms-close" onClick={() => setCancelConfirm(false)}>×</button>
            </div>
            <div className="terms-modal-body">
              <p><strong>Let op!</strong> Hiermee beëindig je je deelnemerschap aan de Hartekind actie 2026.</p>
              <p>Je Strava-koppeling wordt verwijderd en je profiel wordt niet meer getoond op de site.</p>
              <p>Donaties die je achterban al heeft gedaan kunnen niet worden teruggevorderd.</p>
            </div>
            <div className="terms-modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setCancelConfirm(false)}>Annuleren</button>
              <button type="button" className="btn-danger" onClick={cancelParticipation}>Ja, opzeggen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
